import React, { ReactElement, useEffect, useState } from "react";
import * as Middleware from "@middlewares/index";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  About,
  CompanyType,
  Language,
  Direction,
  Specialization,
  Company,
} from "./components";
import { useRouteValidationAlert } from "@helpers/hooks/useNetworkingValidationAlert";
import { validationEndConnectionNetworking } from "@helpers/joinNetwork";
import { getFieldDisplayName } from "@helpers/getFieldDisplayName";
import { MissingField } from "@helpers/validateProfileFills";
import { showAlert } from "@helpers/showAlert";
import * as Model from "@models/index";
import { NetworkingFillStatus } from "@interface/enums";
import { Store } from "@helpers/Store";

type CompleteAlertState = {
  showCompleteAlert?: boolean;
};

interface NetworkingProfile {
  interestCompanyTypes: number[];
  languages: number[];
  directions: number[];
  specializations: number[];
  about: string;
  company: CompanyType | null;
}

interface CompanyType {
  id: number;
  companyImage: string;
  shortTitle: string;
  status: number;
  title: string;
  website: string;
}

const NetworkingPage = (): ReactElement => {
  const history = useHistory();
  const { AlertComponent } = useRouteValidationAlert();
  const [isAlertDepricated, setIsAlertDepricated] = useState(false);
  const location = useLocation();
  const state = location.state as CompleteAlertState;
  const isCompleteAlertVisible = state?.showCompleteAlert ?? false;
  const profile = useSelector((state: any) => state.Profile.data);
  const isUseNetworking = profile.useNetworking;
  const company = profile?.company;
  const languagesList = useSelector((state: any) => state.Language.list);
  const companyTypesList = useSelector((state: any) => state.Company.Type.list);
  const companyDirectionsList = useSelector(
    (state: any) => state.Company.Direction.list
  );
  const companySpecializationsList = useSelector(
    (state: any) => state.Company.Specialization.list
  );
  const [missingFields, setMissingFields] = useState<MissingField[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);

  const buildDefaultProfile = (): NetworkingProfile => ({
    interestCompanyTypes: profile.networkingProfile?.interestCompanyTypes ?? [],
    languages: profile.networkingProfile?.languages ?? [],
    directions: profile.networkingProfile?.directions ?? [],
    specializations: profile.networkingProfile?.specializations ?? [],
    about: profile.networkingProfile?.about ?? "",
    company: profile.company ?? null,
  });

  const [networkingProfile, setNetworkingProfile] = useState<NetworkingProfile>(
    () => ({
      interestCompanyTypes: [],
      languages: [],
      directions: [],
      specializations: [],
      about: "",
      company: null,
    })
  );

  const [defaultNetworkingProfile, setDefaultNetworkingProfile] =
    useState<NetworkingProfile>(() => ({
      interestCompanyTypes: [],
      languages: [],
      directions: [],
      specializations: [],
      about: "",
      company: null,
    }));

  const [hasInit, setHasInit] = useState(false);

  useEffect(() => {
    if (!hasInit && profile.networkingProfile) {
      setNetworkingProfile(buildDefaultProfile());
      setDefaultNetworkingProfile(buildDefaultProfile());
      setHasInit(true);
    }
  }, [hasInit, profile.networkingProfile, profile.company]);

  useEffect(() => {
    setNetworkingProfile((prev) => ({ ...prev, company }));
  }, [company]);

  const handleCancel = () => {
    setEditMode(false);
    setNetworkingProfile(defaultNetworkingProfile);
    setMissingFields([]);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isCompleteAlertVisible) {
      Middleware.Ui.Modal.open("NetworkingComplete", { callback: "" });
    }
    if (!isUseNetworking) {
      setEditMode(true);
    }
  }, []);

  const handleSaveChanges = async () => {
    setMissingFields([]);
    setIsAlertDepricated(true);
    if (
      networkingProfile.languages?.length > 0 &&
      networkingProfile.interestCompanyTypes?.length > 0 &&
      (profile.employmentType === 2 || networkingProfile.company !== null)
    ) {
      if (
        defaultNetworkingProfile?.company?.id &&
        networkingProfile.company?.id !== defaultNetworkingProfile.company.id
      ) {
        await Middleware.Company.Employee.remove(
          defaultNetworkingProfile.company.id,
          profile?.id
        );

        if (profile.employmentType !== 2 && networkingProfile.company) {
          await Middleware.Company.Employee.invite(
            networkingProfile.company.id
          );
        }
        await Middleware.Profile.load();
      }
      await Middleware.Profile.updateAdditional(networkingProfile);
      setEditMode(false);

      return;
    }

    const newMissingFields: MissingField[] = [];

    if (networkingProfile.languages.length === 0) {
      newMissingFields.push("networkingProfilelanguages");
    }
    if (networkingProfile.interestCompanyTypes.length === 0) {
      newMissingFields.push("networkingProfileinterestCompanyTypes");
    }
    if (!networkingProfile.company && profile.employmentType !== 2) {
      newMissingFields.push("companyAssociation");
    }

    setMissingFields(newMissingFields);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    showAlert({
      text: "Заполните недостающие данные!",
      status: "danger",
    });
  };

  const handleJoinToNetworking = async () => {
    if (profile.employmentType !== 2 && networkingProfile.company) {
      await Middleware.Company.Employee.invite(networkingProfile.company.id);
    }

    await Middleware.Profile.updateAdditional(networkingProfile);
    await Middleware.Profile.load();

    const freshProfile = Store.getState().Profile.data;

    const response = await validationEndConnectionNetworking(
      history,
      freshProfile
    );

    if (response) {
      if (response.status === "error") {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }

      if (response.missingFields) {
        setIsAlertDepricated(true);
        setMissingFields(response.missingFields);
      }

      if (!isUseNetworking && response.status === "complete") {
        Middleware.Ui.Modal.open("NetworkingComplete", { callback: "" });
        setEditMode(false);
      }
    }
  };

  const updateNetworkingField =
    (field: keyof typeof networkingProfile) => (value: any) => {
      setNetworkingProfile((prev) => ({ ...prev, [field]: value }));
    };

  const updateCompanyType = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ): void => {
    const value = +event.target.value;

    setNetworkingProfile((prev) => {
      const prevInterests = prev.interestCompanyTypes || [];

      const newInterests = prevInterests.includes(value)
        ? prevInterests.filter((item: number) => item !== value)
        : [...prevInterests, value];

      return {
        ...prev,
        interestCompanyTypes: newInterests,
      };
    });
  };

  const updateLanguage = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ): void => {
    setNetworkingProfile((prev) => {
      const currentLanguages = prev.languages || [];
      const value = +event.target.value;

      const updatedLanguages = currentLanguages.includes(value)
        ? currentLanguages.filter((lang: number) => lang !== value)
        : [...currentLanguages, value];

      return {
        ...prev,
        languages: updatedLanguages,
      };
    });
  };

  const updateSpecialization = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ): void => {
    setNetworkingProfile((prev) => {
      const currentSpecializations = prev.specializations || [];
      const value = +event.target.value;

      const isAlreadyExists = currentSpecializations.includes(value);

      const updatedSpecializations = isAlreadyExists
        ? currentSpecializations.filter((item: number) => item !== value)
        : [...currentSpecializations, value];

      return {
        ...prev,
        specializations: updatedSpecializations,
      };
    });
  };

  const updateCompany = async (event: any) => {
    const newCompanyId = event.target.value;
    const inputValue = event.target.title;

    try {
      const newCompany: any = await new Model.Company().get(newCompanyId);
      setNetworkingProfile((prev) => ({
        ...prev,
        company: newCompany?.data || prev.company,
      }));
    } catch (error) {
      // TODO этот костыль нужно убрать. Model.Company().get иногда не возвращает компанию по id, поэтом так
      try {
        const companyList: any = await new Model.Company().suggest({
          text: inputValue,
        });
        const newCompany = companyList.data.find(
          (item: CompanyType) => item.id === newCompanyId
        );
        setNetworkingProfile((prev) => ({
          ...prev,
          company: newCompany || prev.company,
        }));
      } catch (error) {
        console.error("Ошибка", error);
      }
    }
  };

  const removeDirectionTag = (id: number) => {
    setNetworkingProfile((prev) => {
      const newDirections = prev.directions.filter((item) => item !== id);
      return { ...prev, directions: newDirections };
    });
  };

  const addDirectionTag = (value: number) => {
    setNetworkingProfile((prev) => ({
      ...prev,
      directions: [...prev.directions, value],
    }));
  };

  return (
    <div className="cabinet__layout-col">
      {!isAlertDepricated ? (
        <AlertComponent />
      ) : (
        missingFields.length > 0 && (
          <div className="validation-alert">
            <p>
              Для участия в Нетворкинге, пожалуйста, заполните недостающие
              данные:
            </p>
            <ul>
              {missingFields.map((field) => (
                <li key={field}>{getFieldDisplayName(field)}</li>
              ))}
            </ul>
          </div>
        )
      )}
      <form className="cabinet__block netw-profile">
        <div className="netw-profile__title">
          <h2>
            {isUseNetworking
              ? "Ваши профессиональные интересы"
              : "Анкета для профиля в Нетворкинге"}
          </h2>
          {!editMode && (
            <button
              className="netw-profile__edit-btn"
              onClick={() => {
                setEditMode(true);
              }}
            >
              Изменить
            </button>
          )}
        </div>
        <hr />
        <About
          networkingProfileAbout={networkingProfile?.about}
          onChange={updateNetworkingField("about")}
          editMode={editMode}
          isUseNetworking={isUseNetworking}
        />
        <hr />
        <CompanyType
          interestCompanyTypes={networkingProfile?.interestCompanyTypes}
          onChange={updateCompanyType}
          companyTypesList={companyTypesList}
          editMode={editMode}
          isUseNetworking={isUseNetworking}
          hasError={missingFields.includes(
            "networkingProfileinterestCompanyTypes"
          )}
        />
        <hr />
        <Language
          languages={networkingProfile?.languages}
          onChange={updateLanguage}
          languagesList={languagesList}
          editMode={editMode}
          isUseNetworking={isUseNetworking}
          hasError={missingFields.includes("networkingProfilelanguages")}
        />
        <hr />
        <Direction
          networkingProfile={networkingProfile}
          removeDirectionTag={removeDirectionTag}
          companyDirectionsList={companyDirectionsList}
          editMode={editMode}
          isUseNetworking={isUseNetworking}
          addDirectionTag={addDirectionTag}
        />
        <hr />
        <Specialization
          networkingProfileSpecializations={networkingProfile?.specializations}
          onChange={updateSpecialization}
          companySpecializationsList={companySpecializationsList}
          editMode={editMode}
        />
        {((profile?.employmentType === 1 && isUseNetworking) ||
          profile.networkingFillStatus ===
            NetworkingFillStatus.NeedCompanyAssociation) && (
          <>
            <hr />
            <Company
              networkingProfileCompany={networkingProfile?.company}
              onChange={updateCompany}
              userCompanyStatus={profile?.userModerationStatus}
              hasError={missingFields.includes("companyAssociation")}
              editMode={editMode}
              companyReject={() => updateNetworkingField("company")(null)}
            />
          </>
        )}

        {editMode && isUseNetworking && (
          <div className="flex gap-4 w-full">
            <button
              type="button"
              onClick={() => handleSaveChanges()}
              className="btn w-full btn--medium btn--weight-700  btn--blue"
            >
              Сохранить изменения
            </button>
            <button
              type="button"
              className="btn w-full btn--weight-700 btn--medium btn--grey"
              onClick={() => handleCancel()}
            >
              Отменить изменения
            </button>
          </div>
        )}
        {!isUseNetworking && (
          <div className="flex gap-4 w-full">
            <button
              onClick={() => handleJoinToNetworking()}
              className="btn w-full btn--medium btn--weight-700  btn--blue"
              type="button"
            >
              Сохранить
            </button>
            <Link
              to="/cabinet/networking"
              className="btn w-full btn--weight-700 btn--medium btn--grey"
              type="button"
            >
              Отменить
            </Link>
          </div>
        )}
      </form>
    </div>
  );
};

export default NetworkingPage;
